import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem
} from '@mui/material'
import {
  Visibility,
  Download,
  Edit,
  Delete,
  MoreVert,
  Verified,
  Pending,
  Description,
  Image,
  PictureAsPdf
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { format } from 'date-fns'
import documentService from '../../../services/documentService'

const DocumentList = ({ fileId, onRefresh }) => {
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [documents, setDocuments] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedDoc, setSelectedDoc] = useState(null)
  const [openVerifyDialog, setOpenVerifyDialog] = useState(false)
  const [verificationData, setVerificationData] = useState({
    status: 'VERIFIED',
    remarks: ''
  })

  useEffect(() => {
    if (fileId) {
      loadDocuments()
    }
  }, [fileId, page, rowsPerPage])

  const loadDocuments = async () => {
    setLoading(true)
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage
      }
      
      const response = await documentService.getDocuments(fileId, params)
      
      if (response.data.success) {
        setDocuments(response.data.data)
        setTotalCount(response.data.total || response.data.data.length)
      }
    } catch (error) {
      enqueueSnackbar('Failed to load documents', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleMenuOpen = (event, doc) => {
    setAnchorEl(event.currentTarget)
    setSelectedDoc(doc)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedDoc(null)
  }

  const handleDownload = async (doc) => {
    try {
      const response = await documentService.downloadDocument(doc._id)
      
      // Create blob and download
      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = doc.originalName || `document_${doc._id}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      enqueueSnackbar('Document downloaded successfully', { variant: 'success' })
    } catch (error) {
      enqueueSnackbar('Failed to download document', { variant: 'error' })
    }
    handleMenuClose()
  }

  const handleVerify = async () => {
    try {
      await documentService.verifyDocument(selectedDoc._id, verificationData)
      enqueueSnackbar('Document verified successfully', { variant: 'success' })
      setOpenVerifyDialog(false)
      loadDocuments()
      if (onRefresh) onRefresh()
    } catch (error) {
      enqueueSnackbar('Failed to verify document', { variant: 'error' })
    }
  }

  const handleDelete = async (doc) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await documentService.deleteDocument(doc._id)
        enqueueSnackbar('Document deleted successfully', { variant: 'success' })
        loadDocuments()
        if (onRefresh) onRefresh()
      } catch (error) {
        enqueueSnackbar('Failed to delete document', { variant: 'error' })
      }
    }
    handleMenuClose()
  }

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) {
      return <PictureAsPdf color="error" />
    } else if (mimeType?.includes('image')) {
      return <Image color="primary" />
    } else {
      return <Description color="action" />
    }
  }

  const getVerificationChip = (status) => {
    const config = {
      VERIFIED: { color: 'success', icon: <Verified fontSize="small" /> },
      PENDING: { color: 'warning', icon: <Pending fontSize="small" /> },
      REJECTED: { color: 'error', icon: <Delete fontSize="small" /> }
    }
    
    const { color, icon } = config[status] || { color: 'default', icon: null }
    
    return (
      <Chip
        label={status}
        color={color}
        size="small"
        icon={icon}
      />
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Document Library
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalCount} documents uploaded • View, download, and manage file documents
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={loadDocuments}
          size="large"
        >
          Refresh List
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Documents Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Document</TableCell>
              <TableCell>Type</TableCell>
              {/* <TableCell>Category</TableCell> */}
              <TableCell>Size</TableCell>
              <TableCell>Uploaded</TableCell>
              {/* <TableCell>Status</TableCell> */}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Alert severity="info">
                    No documents found
                  </Alert>
                </TableCell>
              </TableRow>
            ) : (
              documents.map((doc) => (
                <TableRow key={doc._id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getFileIcon(doc.mimeType)}
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {doc.originalName}
                        </Typography>
                        {doc.description && (
                          <Typography variant="caption" color="text.secondary">
                            {doc.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      label={doc.documentType?.replace(/_/g, ' ')}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  
                  {/* <TableCell>
                    <Typography variant="body2">
                      {doc.category?.replace(/_/g, ' ')}
                    </Typography>
                  </TableCell> */}
                  
                  <TableCell>
                    <Typography variant="body2">
                      {doc.fileSize ? `${(doc.fileSize / 1024 / 1024).toFixed(2)} MB` : '-'}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {doc.uploadedAt ? format(new Date(doc.uploadedAt), 'MMM dd, yyyy') : '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {doc.uploadedBy?.name}
                    </Typography>
                  </TableCell>
                  
                  {/* <TableCell>
                    {getVerificationChip(doc.verificationStatus)}
                  </TableCell> */}
                  
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(doc)}
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      {/* <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() => {
                            // Handle view document
                            window.open(`/api/mrd/documents/view/${doc._id}`, '_blank')
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip> */}
                      
                      {doc.verificationStatus === 'PENDING' && (
                        <Tooltip title="Verify">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => {
                              setSelectedDoc(doc)
                              setOpenVerifyDialog(true)
                            }}
                          >
                            <Verified fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, doc)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleDownload(selectedDoc)}>
          <ListItemIcon>
            <Download fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        
        {/* <MenuItem onClick={() => {
          // Handle edit
          handleMenuClose()
        }}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Details</ListItemText>
        </MenuItem> */}
        
        <MenuItem 
          onClick={() => handleDelete(selectedDoc)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Verification Dialog */}
      <Dialog open={openVerifyDialog} onClose={() => setOpenVerifyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Verify Document</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Verification Status</InputLabel>
              <Select
                value={verificationData.status}
                label="Verification Status"
                onChange={(e) => setVerificationData(prev => ({ ...prev, status: e.target.value }))}
              >
                <SelectMenuItem value="VERIFIED">Verified</SelectMenuItem>
                <SelectMenuItem value="REJECTED">Rejected</SelectMenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Remarks"
              value={verificationData.remarks}
              onChange={(e) => setVerificationData(prev => ({ ...prev, remarks: e.target.value }))}
              placeholder="Enter verification remarks..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVerifyDialog(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleVerify}>
            Verify
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DocumentList